/**
 * Check-in Business Logic
 * Handles USAW validation, subscription checks, and check-in posting
 */

// Error codes from Api::Team::V1::CheckInsController for check-ins that need an
// admin override (see CheckInEligibility).
const CHECK_IN_BLOCKER_CODES = ['subscription_paused', 'subscription_overdue', 'weekly_limit_reached'];

// The blocker code from a failed check-in POST, or null for any other error.
export function checkInBlockerCode(error) {
  const code = error?.response?.status === 403 ? error.response.data?.error : null;
  return CHECK_IN_BLOCKER_CODES.includes(code) ? code : null;
}

export function useCheckInLogic(store, apiService, notificationManager) {
  function missingUsawMembership(wrestler) {
    if (!wrestler.usaw_membership_id || !wrestler.usaw_expires_on) {
      return true;
    }

    return moment(wrestler.usaw_expires_on).isBefore(moment());
  }

  function missingAauMembership(wrestler) {
    if (!wrestler.aau_membership_id || !wrestler.aau_expires_on) {
      return true;
    }

    return moment(wrestler.aau_expires_on).isBefore(moment());
  }

  async function postCheckIn(wrestler, event, source = 'kiosk_self', weightEntry = null) {
    // Subscription paused/overdue and the weekly class limit are enforced
    // server-side per event. Those 403s surface as override modals in
    // KioskEventSelector, so they don't get the generic failure toast.

    // Fire off a check in for this wrestler
    const params = {
      check_in: {
        wrestler_profile_id: wrestler.id,
        status: 'present',
      },
      // Audit trail for how this check-in was created. Defaults to a normal kiosk
      // self check-in; the attendees/calendar admin add passes 'kiosk_admin_calendar'.
      source,
    };

    // Weight tracking: { weight, units } collected by KioskWeightModal, created
    // server-side alongside the check-in
    if (weightEntry) {
      params.check_in.weight_entry = weightEntry;
    }

    // Thread in the event if we are doing that
    if (event && event.id !== -1) {
      params.event_id = event.id;
    }

    const url = apiService.checkInUrl();

    return axios.post(url, params)
      .then((response) => {
        store.addToCheckIns(response.data);
        return response.data;
      })
      .catch((error) => {
        console.error(error);
        if (!checkInBlockerCode(error)) {
          notificationManager.emit('show-toast', 'Failed to check in. Please try again.', false);
        }
        return Promise.reject(error);
      });
  }

  async function postCheckInWithOverride(wrestler, event, adminPin, kioskTokenId, overrideReason, weightEntry = null) {
    // Fire off a check in for this wrestler with admin override
    const params = {
      check_in: {
        wrestler_profile_id: wrestler.id,
        status: 'present',
      },
      admin_pin: adminPin,
      kiosk_token_id: kioskTokenId,
      override_reason: overrideReason,
    };

    // Weight tracking: still collected on the override path
    if (weightEntry) {
      params.check_in.weight_entry = weightEntry;
    }

    // Thread in the event if we are doing that
    if (event && event.id !== -1) {
      params.event_id = event.id;
    }

    const url = apiService.checkInUrl();

    return axios.post(url, params)
      .then((response) => {
        store.addToCheckIns(response.data);
        return response.data;
      });
    // Note: Don't catch here - let the caller handle errors for PIN validation feedback
  }

  return {
    missingUsawMembership,
    missingAauMembership,
    checkInBlockerCode,
    postCheckIn,
    postCheckInWithOverride,
  };
}
